import React, { useEffect, useState } from 'react'
import { Box, Divider, IconButton } from '@mui/material'
import {
  ALMOST_THERE,
  ApplicationContainerProps,
  ApplicationProgress,
  Step
} from './types'
import {
  getApplicationStep,
  hasApplicationRejected,
  hasApplicationSubmitted,
  hasCompletedCreditApplication,
  isReady,
  isWaiting,
  sendTrackingEventsForSteps,
  setPreviousStep,
  updateCurrentStep,
  userDeclined
} from '../../services/creditApplication.service'
import {
  isWaiting as CardIsWaiting,
  isReady as CardIsReady
} from '../../services/creditCard.service'
import { CreditApplicationCompleted } from './ApplicationCompleted/ApplicaitonCompleted.container'
import { ProgressStepper } from '../ProgressStepper/Progress.component'
import { ChevronLeft } from '@mui/icons-material'
import { ApplicationSubmittedContainer } from './ApplicationSubmitted/ApplicationSubmitted.container'
import { CreditApplication } from '../../domain/creditApplication'

import FormContainer from '../FormContainer/FormContainer'
import { ApplicationReadyContainer } from './ApplicationReady/ApplicationReady.container'
import { ApplicationErrorComponent } from './ApplicationSubmitted/ApplicationError.component'
import { CreditApplicationError } from './ApplicationSubmitted/types'
import {
  CreditAcceptResponse,
  CreditCardDetails
} from '../../domain/creditCard'
import { CreditCardSubmitted } from './CreditCardSubmitted/CreditCardSubmitted.container'
import { CreditCardErrorComponent } from './CreditCardSubmitted/CreditCardError.component'
import { CreditCardError } from './CreditCardSubmitted/types'
import { CreditCardReadyComponent } from './CreditCardSubmitted/CreditCardReady.component'
import { PaperWrapper } from '../DisplayHelpers/PaperWrapper'
import { ApplicationOfferDeclineContainer } from './ApplicationOfferDeclined/ApplicationOfferDeclined.container'

type State = ApplicationProgress

export const ApplicationProgressContainer: React.FC<
  ApplicationContainerProps
> = ({ creditApplications, initialState }) => {
  const [submittedApplication, setSubmittedApp] =
    useState<CreditApplication | null>(
      hasApplicationSubmitted(creditApplications)
    )

  const [cardSubmitted, setCardSubmitted] =
    useState<CreditAcceptResponse | null>(null)

  const [creditStates, setCreditStates] = useState<State>(initialState)

  const [applicationError, setApplicationError] =
    useState<CreditApplicationError | null>(
      hasApplicationRejected(submittedApplication)
    )

  const [cardError, setCardError] = useState<CreditCardError | null>(null)
  const [creditCard, setCreditCard] = useState<CreditCardDetails | null>(null)

  const handleCardSuccess = (card: CreditCardDetails) => {
    if (cardSubmitted && CardIsReady(card.status)) {
      setCardSubmitted({ ...cardSubmitted, status: card.status })
      setCreditCard(card)
    }
  }

  const handleTransition = (nextStep: number, updatedStep?: Step) => {
    if (updatedStep) {
      return setCreditStates({
        ...creditStates,
        steps: updateCurrentStep(creditStates.steps, updatedStep),
        currentStep: getApplicationStep(nextStep, creditStates.steps),
        prevStep:
          updatedStep.step === ALMOST_THERE.step
            ? ALMOST_THERE.step
            : setPreviousStep(nextStep)
      })
    }
    return setCreditStates({
      ...creditStates,
      currentStep: getApplicationStep(nextStep, creditStates.steps),
      prevStep: setPreviousStep(nextStep)
    })
  }

  useEffect(() => {
    if (
      !hasCompletedCreditApplication(creditApplications) &&
      !submittedApplication &&
      !cardSubmitted
    ) {
      sendTrackingEventsForSteps(creditStates)
    }
  }, [creditStates.currentStep])

  /**
   * Will return here if user closes the application or returns to it
   * and has accepted the credit offer
   */
  if (hasCompletedCreditApplication(creditApplications)) {
    return (
      <CreditApplicationCompleted creditApplications={creditApplications} />
    )
  }

  /**
   * Upon submisson or has submited and not accepted offer
   * render components below
   */
  if (submittedApplication && !cardSubmitted) {
    return (
      <PaperWrapper includeBox>
        {isWaiting(submittedApplication.status) && !applicationError && (
          <ApplicationSubmittedContainer
            applicationId={submittedApplication.id}
            handleError={setApplicationError}
            handleReady={setSubmittedApp}
          />
        )}

        {isReady(submittedApplication.status) && (
          <ApplicationReadyContainer
            applicationId={submittedApplication.id}
            setCardSubmitted={setCardSubmitted}
            handleError={setApplicationError}
          />
        )}

        {userDeclined(submittedApplication.status) && (
          <ApplicationOfferDeclineContainer />
        )}

        {applicationError && (
          <ApplicationErrorComponent error={applicationError} />
        )}
      </PaperWrapper>
    )
  }

  /**
   * Application Submitted and card submitted
   */
  if (submittedApplication && cardSubmitted) {
    return (
      <PaperWrapper includeBox>
        {CardIsWaiting(cardSubmitted.status) && !cardError && (
          <CreditCardSubmitted
            cardId={cardSubmitted.id}
            handleReady={handleCardSuccess}
            handleError={setCardError}
          />
        )}

        {creditCard && CardIsReady(creditCard.status) && (
          <CreditCardReadyComponent cardDetails={creditCard} />
        )}

        {cardError && <CreditCardErrorComponent error={cardError} />}
      </PaperWrapper>
    )
  }

  /**
   * Default state if credit card is not created or no application
   * has been submitted
   */
  return (
    <PaperWrapper includeBox>
      <Box sx={{ mt: -4, mb: 2, display: 'flex', pr: 4, pl: 4 }}>
        {creditStates.currentStep.hasBackTransition && (
          <IconButton
            size="small"
            color="primary"
            sx={{ ml: -4 }}
            onClick={() => handleTransition(creditStates.currentStep.step - 1)}
          >
            <ChevronLeft />
          </IconButton>
        )}
        <ProgressStepper
          step={creditStates.currentStep.step}
          totalSteps={creditStates.totalSteps}
        />
      </Box>
      <Divider sx={{ mt: -2.5, mr: -5, ml: -5 }} variant="fullWidth" />
      <Box sx={{ mt: 2 }}>
        {creditStates.currentStep.FormComponent && (
          <FormContainer
            title={creditStates.currentStep.title}
            description={creditStates.currentStep.description}
            handleClick={handleTransition}
            stepState={creditStates.currentStep}
            FormComponent={creditStates.currentStep.FormComponent}
            applicationProgress={creditStates}
            setSubmittedAppication={setSubmittedApp}
          />
        )}
      </Box>
    </PaperWrapper>
  )
}
